import { useState, useCallback, useRef } from "react";
import { ShipmentFileResponseDto } from "../dtos/types/ShipmentFile";

/**
 * Manages preview state: which file is open, its blob URL, loading/error status.
 * Blob URLs are cached by fileId so repeated opens are instant.
 */
export function useFilePreview(
  /** Async function that fetches a blob (ArrayBuffer / Blob) for a given file */
  fetchBlob: (shipmentId: string, fileId: string) => Promise<{ data: ArrayBuffer | Blob }>,
  shipmentId: string,
) {
  const [previewFile, setPreviewFile] = useState<ShipmentFileResponseDto | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | undefined>();
  const [loadingPreview, setLoading] = useState(false);
  /** Cache: fileId -> objectURL */
  const cache = useRef<Record<string, string>>({});

  const openPreview = useCallback(
    async (file: ShipmentFileResponseDto) => {
      setPreviewFile(file);
      setBlobUrl(undefined);

      if (cache.current[file.id]) {
        setBlobUrl(cache.current[file.id]);
        return;
      }

      setLoading(true);
      try {
        const res = await fetchBlob(shipmentId, file.id);
        const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: file.contentType ?? "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        cache.current[file.id] = url;
        setBlobUrl(url);
      } catch {
        setBlobUrl(undefined);
      } finally {
        setLoading(false);
      }
    },
    [fetchBlob, shipmentId],
  );

  const closePreview = useCallback(() => {
    setPreviewFile(null);
    setBlobUrl(undefined);
  }, []);

  return { previewFile, blobUrl, loadingPreview, openPreview, closePreview };
}
